import {GeneratedDomainGraphInterface} from './GeneratedDomainGraphInterface';
import {GeneratedDomainGraphType, ID_PREFIX_TO_DOMAIN_GRAPH_TYPES_ENTRIES} from './GeneratedDomainGraphType';

/**
 * A string which represents the id in a CoreObject type interface. Uses `{__coreObject: never}` as a trick to
 * convince TypeScript that these strings are in some way more specific than standard strings.
 *
 * See CoreObjectIntrospectionUtils for where the type narrowing predicate is implemented and used. In effect,
 * this requires the use of the predicate before using any of the other utils.
 */
export type CoreObjectId = string & {__coreObject: never};

/**
 * Workaround for "Object literal may only specify known properties". See
 * https://github.com/Microsoft/TypeScript/issues/3755 for more context.
 */
interface ExtraCoreObjectProperties {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface CoreObjectBase {
  id: CoreObjectId;
}

/**
 * Minimum interface for CoreObjects. For now only specifies an id field that is artificially more narrow than
 * a string in a type sense, but in practice is just a string at runtime.
 */
export type CoreObject = CoreObjectBase & ExtraCoreObjectProperties;

export interface MaybeCoreObjectBase {
  id: string;
}

export type MaybeCoreObject = MaybeCoreObjectBase & ExtraCoreObjectProperties;

/**
 * Enum of canonical object types.
 */
export const DomainGraphType = GeneratedDomainGraphType;

/**
 * TypeScript type that represents the union of object type identity strings. These strings are used in a
 * number of other mappings.
 */
export type ObjectType = keyof typeof DomainGraphType;

/**
 * TypeScript type that represents the union of API ID prefix strings.
 */
export type CoreObjectIdPrefix = (typeof ID_PREFIX_TO_DOMAIN_GRAPH_TYPES_ENTRIES)[number][0];

/**
 * Enum of canonical capability types.
 */
export const DomainGraphInterface = GeneratedDomainGraphInterface;

/**
 * TypeScript type that represents the union of canonical capability interface strings. These strings are used
 * in a number of other mappings.
 */
export type InterfaceType = keyof typeof DomainGraphInterface;
